import { Component, OnDestroy, OnInit } from '@angular/core';
import { IContract, IContractAccountsCollection } from '@models/contract.class';
import { EventSuccess } from '@models/tracking-success-event.class';
import { Store } from '@ngrx/store';
import { isEmpty } from 'lodash';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, take, tap } from 'rxjs/operators';
import {
  AppState,
  DEFAULT_STRATEGY_NAME,
  PORTFOLIO,
  TRACKING_EVENTS,
} from 'src/app';
import { selectContractsAccountsAccounts } from 'src/app/state/selectors/contract-accounts-selectors';
import { selectIsUsa } from 'src/app/state/selectors/current-contract.selectors';
import { ContractsService } from 'src/core';
import { MixpanelService } from 'src/core/services/mixpanel.service';
import { SessionService } from 'src/core/services/session.service';

@Component({
  selector: 'hb-contracts-combo',
  templateUrl: 'contractsCombo.component.html',
  styleUrls: ['./contractsCombo.component.scss'],
})
export class ContractsComboComponent implements OnInit, OnDestroy {
  moreThanOneContract: boolean = undefined;
  contracts: any[] = [];
  selectedContract: string;
  bootstrapPromise: Promise<any>;

  public selectedContractName: string;
  public showItems = false;

  private lastContractShown: string;
  private contractsSubscription = new Subscription();

  private contractAccountsCollection: IContractAccountsCollection;

  constructor(
    private store: Store<AppState>,
    private contractsService: ContractsService,
    private readonly mixpanelService: MixpanelService,
    private readonly sessionService: SessionService,
  ) {}

  ngOnInit() {
    this.init();
  }

  ngOnDestroy() {
    this.contractsSubscription.unsubscribe();
  }

  changeContract(contractId: string): void {
    this.selectedContract = contractId;
    this.showItems = false;
    if (this.lastContractShown !== contractId) {
      this.selectedContractName = undefined;

      this.contractsSubscription.add(
        this.contractsService.getContractInfo(contractId).subscribe(
          (result) => {
            if (!result) {
              this.selectedContract = this.lastContractShown;
              this.changeContract(this.selectedContract);
            } else {
              this.store.dispatch({
                type: PORTFOLIO,
                payload: {},
              });
            }
          },
          () => undefined,
          () => {
            this.sessionService.setCurrentContract(this.selectedContract);
            this.contractsSubscription.add(
              this.store
                .select(selectIsUsa)
                .pipe(take(1))
                .subscribe((isUsa) => {
                  const successEvent = new EventSuccess();
                  successEvent.setType(isUsa);
                  const successEventData = successEvent.getEventSuccess();

                  this.mixpanelService.track(
                    TRACKING_EVENTS.account_detail.event,
                    successEventData,
                  );
                }),
            );
          },
        ),
      );
    }
  }

  public toggleCombo() {
    this.showItems = !this.showItems;
  }

  private init(): void {
    this.getCurrentComContractAndAccount();

    const nextSearch$ = new Subject();
    const strategyDefaultName = 'Trading';

    this.contractsSubscription.add(
      this.store
        .select('currentContract')
        .pipe(
          map((currentContract: any) => currentContract.toJS()),
          filter((currentContract: IContract) => !isEmpty(currentContract)),
          distinctUntilChanged((a: IContract, b: IContract) => {
            const isSameContract = a.contractID === b.contractID;
            const isSameLoremAccount = a.isLoremAccount === b.isLoremAccount;
            return isSameContract && isSameLoremAccount;
          }),
          map((currentContract) => {
            const contractChanged: boolean =
              !this.selectedContract ||
              this.selectedContract !== currentContract.contractID;
            return [currentContract, contractChanged];
          }),
          tap(([currentContract, contractChanged]: [IContract, boolean]) => {
            if (contractChanged) {
              nextSearch$.next(currentContract.contractID);
            }
          }),
        )
        .subscribe(([currentContract]: [IContract, boolean]) => {
          this.selectedContract = currentContract.contractID;
          this.lastContractShown = currentContract.contractID;
          if (currentContract.loremAccountLoaded) {
            !currentContract.isLoremAccount
              ? (this.selectedContractName = strategyDefaultName)
              : this.getContractName();
          }
        }),
    );
  }

  private getContractName() {
    if (!this.contractAccountsCollection) {
      setTimeout(() => {
        this.getContractName();
      }, 500);
    } else {
      this.selectedContractName = this.contractAccountsCollection.accounts[
        this.selectedContract
      ]
        ? this.contractAccountsCollection.accounts[this.selectedContract].name
        : undefined;
    }
  }

  private getCurrentComContractAndAccount() {
    this.contractsSubscription.add(
      combineLatest(
        this.store
          .select('contracts')
          .pipe(map((values: any) => values.toJS())),
        this.store.select(selectContractsAccountsAccounts),
      ).subscribe(([contracts, accounts]) => {
        this.moreThanOneContract = contracts.length > 1;

        if (accounts) {
          !this.contractAccountsCollection &&
            (this.contractAccountsCollection =
              {} as IContractAccountsCollection);

          this.contractAccountsCollection.accounts = accounts;
        }
        if (this.moreThanOneContract) {
          this.contracts = contracts;
          this.contracts = this.contracts.map((item) => {
            const strategyName =
              accounts && accounts[item.contractId]
                ? accounts[item.contractId].name
                : DEFAULT_STRATEGY_NAME;

            return {
              ...item,
              strategyName,
            };
          });
        }
        this.getContractName();
      }),
    );
  }
}
